import { useEffect, useState } from 'react';
import { BREAKPOINT } from '../constants/constants';
import { useContentQuery } from './UseContentWidth';

/**
 * 좁은 폭 기준 — 이 값 미만이면 사이드바를 Drawer 로 접는다.
 * 220px 사이드바를 그대로 두면 390px 화면에서 본문이 122px 밖에 안 남아
 * 표·툴바가 전부 가로로 넘쳤다(2026-10-19 감사 "화면 · 좁은 폭"). 디자인 시스템 v2(사장 결정 2026-11-04)로 999 → 1023(desktop 1024 부터).
 */
export const COMPACT_MAX_WIDTH = BREAKPOINT.desktop - 1;

const QUERY = `(max-width: ${COMPACT_MAX_WIDTH}px)`;

/**
 * 세로로 쌓아야 하는 휴대폰 폭 기준 — 390px 기준 화면(코덱 3차 지적, 2026-10-20).
 *
 * compact(≤999px)는 "사이드바를 Drawer 로 접는" 기준이라 태블릿 폭까지 포함한다.
 * 가로 배치가 자리 부족으로 잘리는 건 그보다 좁은 구간이라, 세로 쌓기·입력 폭
 * 100%·헤더 버튼 44px 같은 처리는 이 더 좁은 기준을 따로 본다.
 */
export const PHONE_MAX_WIDTH = BREAKPOINT.tablet - 1; // v2 로 640 → 599(phone < 600)

const PHONE_QUERY = `(max-width: ${PHONE_MAX_WIDTH}px)`;

const readMatches = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(QUERY).matches;
};

const readPhoneMatches = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(PHONE_QUERY).matches;
};

/**
 * 좁은 폭(<= 1023px) 여부.
 *
 * window.matchMedia 를 구독하므로 창 크기를 바꾸면 즉시 다시 렌더된다.
 * (Ant Grid 의 useBreakpoint 는 기본 브레이크포인트가 992/1200 이라 1000px 기준을
 *  그대로 표현할 수 없어 matchMedia 를 직접 쓴다.)
 */
export const useIsCompact = (): boolean => {
  const [compact, setCompact] = useState<boolean>(readMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setCompact(e.matches);
    setCompact(mql.matches);

    // 구형 Safari 는 MediaQueryList.addEventListener 가 없다 — addListener 로 폴백.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return compact;
};

/**
 * 휴대폰 폭(< 600px) 여부.
 *
 * 390px 화면에서 한 줄로 못 들어가는 요소(①~③ 단계 안내, 설정 입력 라벨/값, 헤더 버튼)를
 * 세로로 쌓거나 키울 때 쓴다. 구독 방식은 `useIsCompact`와 같다.
 */
export const useIsPhone = (): boolean => {
  const [phone, setPhone] = useState<boolean>(readPhoneMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia(PHONE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setPhone(e.matches);
    setPhone(mql.matches);

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return phone;
};

/**
 * 넓은 본문 여부 — "매장가 요청" 표가 처리 버튼 둘을 한 줄에 나란히 둘 만큼 넓은가(감사 card-controltower#326). 표 열 합(약 1,440px)이
 * 들어가는 **본문 폭** 1440 이상(v2 — 예전엔 화면 폭 1740 = 사이드바 220 · 여백 48 · 스크롤바를 손으로 더한 값이었고, 본문 최대 폭이
 * 생기자 틀렸다). 아직 본문을 재지 못했으면(첫 그리기 · 본문 상자 없는 단위 테스트) 예전 화면 폭 질의를 쓴다.
 */
export const WIDE_MIN_CONTENT_WIDTH = 1440;
/** 예전 화면 폭 기준(재기 전 대체 · 단위 테스트의 matchMedia 흉내) — L3b 정리 때 없앤다. */
export const WIDE_MIN_WIDTH = 1740;

export const useIsWide = (): boolean =>
  useContentQuery((w) => w >= WIDE_MIN_CONTENT_WIDTH, `(min-width: ${WIDE_MIN_WIDTH}px)`);